import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { FileText, Loader2 } from 'lucide-react';
import axiosInstance from '../lib/axios';

// Google Identity Services (GIS) — official client script.
const GIS_SRC = 'https://accounts.google.com/gsi/client';
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

// ponytail: load the GIS script once, dynamically, so the feature stays fully
// self-contained and disabled when no client_id is configured.
const loadGisScript = () =>
  new Promise((resolve, reject) => {
    if (window.google?.accounts?.id) return resolve();
    const existing = document.querySelector(`script[src="${GIS_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', reject);
      return;
    }
    const script = document.createElement('script');
    script.src = GIS_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = reject;
    document.head.appendChild(script);
  });

// Logo "G" de Google (4 colores) inline como SVG — sin peticiones externas.
const GoogleGIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

const AuthPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  // 'auth' = login/registro; 'forgot' = pedir enlace; 'reset' = fijar nueva pass (desde el enlace)
  const [view, setView] = useState('auth');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');

  // Si la URL trae ?token=… (enlace del correo), entra directo a la vista reset.
  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get('token');
    if (token) { setResetToken(token); setView('reset'); }
  }, []);

  const handleForgot = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axiosInstance.post('/auth/forgot-password', { email });
      toast.success('Si el correo existe, te enviamos un enlace para continuar.');
      setView('auth');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar el enlace');
    } finally { setLoading(false); }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axiosInstance.post('/auth/reset-password', { token: resetToken, new_password: newPassword });
      toast.success('Contraseña actualizada. Inicia sesión con la nueva.');
      setResetToken(''); setNewPassword(''); setPassword('');
      window.history.replaceState({}, '', '/'); // limpia el ?token= de la URL
      setView('auth'); setIsLogin(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'El enlace es inválido o expiró');
    } finally { setLoading(false); }
  };
  const { login, register, loginWithGoogle } = useAuthStore();
  const navigate = useNavigate();
  const googleButtonRef = useRef(null);

  const handleGoogleCredential = useCallback(
    async (response) => {
      if (!response?.credential) return;
      setGoogleLoading(true);
      try {
        await loginWithGoogle(response.credential);
        toast.success('¡Bienvenido!');
        navigate('/dashboard');
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Error al iniciar sesión con Google');
      } finally {
        setGoogleLoading(false);
      }
    },
    [loginWithGoogle, navigate]
  );

  useEffect(() => {
    // Feature hidden unless a client_id is provided at build time. El botón solo
    // existe en la vista 'auth'; en forgot/reset el ref no está montado.
    if (!GOOGLE_CLIENT_ID || view !== 'auth') return;

    let cancelled = false;
    loadGisScript()
      .then(() => {
        if (cancelled || !googleButtonRef.current) return;
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleGoogleCredential,
          auto_select: false,           // no auto-login silencioso
          use_fedcm_for_prompt: true,   // compatible con el bloqueo de 3rd-party cookies
        });
        const el = googleButtonRef.current;
        el.innerHTML = '';  // limpia el render previo al re-renderizar (cambio login/registro)
        // GIS exige width en PÍXELES (número ≤400); '100%' es inválido y cae al
        // ancho por defecto. Medimos el contenedor para que el botón sea full-width.
        const width = Math.min(400, Math.round(el.offsetWidth) || 360);
        window.google.accounts.id.renderButton(el, {
          theme: 'outline',
          size: 'large',
          width,
          text: isLogin ? 'signin_with' : 'signup_with',
          shape: 'rectangular',
          logo_alignment: 'center',
        });
      })
      .catch(() => {
        if (!cancelled) toast.error('No se pudo cargar el inicio de sesión con Google');
      });

    return () => {
      cancelled = true;
    };
  }, [handleGoogleCredential, isLogin, view]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        await login(email, password);
        toast.success('¡Bienvenido de nuevo!');
      } else {
        await register(email, password);
        toast.success('¡Cuenta creada exitosamente!');
      }
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error en la autenticación');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 app-gradient">
      <Card className="w-full max-w-md glass border-0 shadow-2xl" data-testid="auth-card">
        <CardHeader className="text-center space-y-4">
          <div className="flex justify-center">
            <div className="bg-gradient-to-br from-sky-400 to-blue-500 p-4 rounded-2xl shadow-lg">
              <FileText className="w-12 h-12 text-white" />
            </div>
          </div>
          <CardTitle className="text-4xl" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            SapiensDoc
          </CardTitle>
          <CardDescription className="text-base">
            {view === 'forgot' ? 'Recupera tu contraseña'
              : view === 'reset' ? 'Elige una nueva contraseña'
              : isLogin ? 'Inicia sesión para continuar' : 'Crea tu cuenta'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {view === 'auth' && (<>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium" htmlFor="email">Email</label>
              <Input
                id="email"
                data-testid="email-input"
                type="email"
                placeholder="tu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="transition-all focus:ring-2 focus:ring-sky-500"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium" htmlFor="password">Contraseña</label>
              <Input
                id="password"
                data-testid="password-input"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="transition-all focus:ring-2 focus:ring-sky-500"
              />
            </div>
            <Button
              data-testid="auth-submit-button"
              type="submit"
              className="w-full bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white font-medium py-6 rounded-lg shadow-lg hover:shadow-xl transition-all"
              disabled={loading || googleLoading}
            >
              {loading ? 'Procesando...' : isLogin ? 'Iniciar Sesión' : 'Crear Cuenta'}
            </Button>
            {isLogin && (
              <button type="button" data-testid="forgot-password-link" onClick={() => setView('forgot')}
                className="w-full text-center text-sm text-sky-600 hover:text-sky-700 dark:text-sky-400">
                ¿Olvidaste tu contraseña?
              </button>
            )}
          </form>
          {(GOOGLE_CLIENT_ID || process.env.NODE_ENV !== 'production') && (
          <div className="mt-6">
            <div className="relative flex items-center justify-center">
              <span className="absolute inset-x-0 h-px bg-gray-200 dark:bg-slate-600" />
              <span className="relative bg-white dark:bg-slate-800 px-3 text-xs text-gray-400 dark:text-slate-400">o continúa con</span>
            </div>
            {GOOGLE_CLIENT_ID ? (
              <div className="relative mt-4">
                <div
                  ref={googleButtonRef}
                  data-testid="google-signin-button"
                  className={`flex justify-center transition-opacity ${googleLoading ? 'opacity-40 pointer-events-none' : ''}`}
                />
                {googleLoading && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
                      <Loader2 className="w-4 h-4 animate-spin" /> Conectando con Google…
                    </span>
                  </div>
                )}
              </div>
            ) : process.env.NODE_ENV !== 'production' ? (
              // Vista previa SOLO en desarrollo (sin Client ID): botón deshabilitado
              // para revisar el layout. No hace login; se activa al definir
              // REACT_APP_GOOGLE_CLIENT_ID. En prod sin configurar, no se muestra nada.
              <div className="mt-4">
                <button
                  type="button"
                  disabled
                  data-testid="google-signin-placeholder"
                  title="Vista previa — configura REACT_APP_GOOGLE_CLIENT_ID para activarlo"
                  className="w-full flex items-center justify-center gap-3 h-11 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm font-medium text-slate-700 dark:text-slate-200 cursor-not-allowed opacity-70"
                >
                  <GoogleGIcon /> {isLogin ? 'Iniciar sesión con Google' : 'Registrarse con Google'}
                </button>
                <p className="mt-2 text-center text-[11px] text-slate-400 dark:text-slate-500">
                  Vista previa · pendiente de configurar Google
                </p>
              </div>
            ) : null}
          </div>
          )}
          <div className="mt-6 text-center">
            <button
              data-testid="toggle-auth-mode"
              onClick={() => setIsLogin(!isLogin)}
              disabled={loading || googleLoading}
              className="text-sky-600 hover:text-sky-700 dark:text-sky-400 dark:hover:text-sky-300 font-medium transition-colors disabled:opacity-50"
            >
              {isLogin ? '¿No tienes cuenta? Regístrate' : '¿Ya tienes cuenta? Inicia sesión'}
            </button>
          </div>
          </>)}

          {view === 'forgot' && (
            <form onSubmit={handleForgot} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium" htmlFor="forgot-email">Email</label>
                <Input
                  id="forgot-email"
                  data-testid="forgot-email-input"
                  type="email"
                  placeholder="tu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="transition-all focus:ring-2 focus:ring-sky-500"
                />
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Te enviaremos un enlace para elegir una nueva contraseña.
              </p>
              <Button
                data-testid="forgot-submit-button"
                type="submit"
                className="w-full bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white font-medium py-6 rounded-lg shadow-lg hover:shadow-xl transition-all"
                disabled={loading}
              >
                {loading ? 'Enviando...' : 'Enviar enlace'}
              </Button>
              <div className="text-center">
                <button type="button" onClick={() => setView('auth')}
                  className="text-sm text-sky-600 hover:text-sky-700 dark:text-sky-400">
                  ← Volver al inicio de sesión
                </button>
              </div>
            </form>
          )}

          {view === 'reset' && (
            <form onSubmit={handleReset} className="space-y-4">
              {!resetToken && (
                <p className="text-sm text-red-600 dark:text-red-400">
                  Falta el enlace de restablecimiento. Ábrelo desde el correo que te enviamos.
                </p>
              )}
              <div className="space-y-2">
                <label className="text-sm font-medium" htmlFor="reset-newpass">Nueva contraseña</label>
                <Input
                  id="reset-newpass"
                  data-testid="reset-newpass-input"
                  type="password"
                  placeholder="••••••••"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={8}
                  className="transition-all focus:ring-2 focus:ring-sky-500"
                />
                <p className="text-xs text-slate-500 dark:text-slate-400">Mínimo 8 caracteres.</p>
              </div>
              <Button
                data-testid="reset-submit-button"
                type="submit"
                className="w-full bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white font-medium py-6 rounded-lg shadow-lg hover:shadow-xl transition-all"
                disabled={loading || !resetToken}
              >
                {loading ? 'Procesando...' : 'Restablecer contraseña'}
              </Button>
              <div className="text-center">
                <button type="button" onClick={() => setView('auth')}
                  className="text-sm text-sky-600 hover:text-sky-700 dark:text-sky-400">
                  ← Volver al inicio de sesión
                </button>
              </div>
            </form>
          )}

          <p className="mt-4 text-center text-xs text-slate-500 dark:text-slate-400">
            Al continuar aceptas los{' '}
            <a href="/legal/terms" target="_blank" rel="noopener noreferrer" className="underline hover:text-sky-600 dark:hover:text-sky-400">
              Términos y Condiciones
            </a>{' '}
            y el{' '}
            <a href="/legal/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-sky-600 dark:hover:text-sky-400">
              Aviso de Privacidad
            </a>.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default AuthPage;
